import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import MainLayout from "@/components/MainLayout";

export default function NumberAssets() {
  const navigate = useNavigate();
  const [numberSets, setNumberSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newSetName, setNewSetName] = useState("");
  const [minNumber, setMinNumber] = useState(0);
  const [maxNumber, setMaxNumber] = useState(9);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [setToDelete, setSetToDelete] = useState(null);

  useEffect(() => {
    loadNumberSets();
  }, []);

  const loadNumberSets = async () => {
    try {
      const response = await axios.get(`${API}/number-sets`);
      setNumberSets(response.data);
      if (response.data.length > 0 && !selectedSet) {
        setSelectedSet(response.data[0]);
      }
    } catch (error) {
      toast.error("Failed to load number sets");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSet = async () => {
    if (!newSetName.trim()) {
      toast.error("Please enter a set name");
      return;
    }

    if (minNumber < 0 || maxNumber > 199) {
      toast.error("Numbers must be between 0 and 199");
      return;
    }

    if (minNumber > maxNumber) {
      toast.error("Minimum number cannot be greater than maximum number");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", newSetName);
      formData.append("min_number", minNumber);
      formData.append("max_number", maxNumber);
      const response = await axios.post(`${API}/number-sets`, formData);
      toast.success("Number set created successfully");
      setNewSetName("");
      setMinNumber(0);
      setMaxNumber(9);
      setIsDialogOpen(false);
      loadNumberSets();
      setSelectedSet(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create number set");
    }
  };

  const handleUpload = async (number, file) => {
    if (!file || !selectedSet) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("number", number);
    formData.append("image", file);

    try {
      const response = await axios.post(`${API}/number-sets/${selectedSet.id}/upload`, formData);
      toast.success(`Number ${number} uploaded successfully`);
      setSelectedSet(response.data);
      loadNumberSets();
    } catch (error) {
      toast.error(`Failed to upload number ${number}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (number) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) handleUpload(number, file);
    };
    input.click();
  };

  const handleDeleteSet = async (setId) => {
    setSetToDelete(setId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!setToDelete) return;

    try {
      await axios.delete(`${API}/number-sets/${setToDelete}`);
      toast.success("Number set deleted successfully");
      setDeleteDialogOpen(false);
      setSetToDelete(null);
      loadNumberSets();
      setSelectedSet(null);
    } catch (error) {
      toast.error("Failed to delete number set");
      setDeleteDialogOpen(false);
      setSetToDelete(null);
    }
  };

  if (loading) {
    return (
      <MainLayout title="Number Assets" subtitle="Manage your number image sets">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </MainLayout>
    );
  }

  const headerActions = (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="create-set-button"
          className="bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          + Create New Set
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0F172A] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-foreground">Create New Number Set</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="set-name">Set Name</Label>
            <Input
                      id="set-name"
                      value={newSetName}
                      onChange={(e) => setNewSetName(e.target.value)}
                      placeholder="e.g., Blue Balls, Gold Set"
                      className="mt-2 bg-secondary/50 border-white/10"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="min-number">Lowest Number</Label>
                      <Input
                        id="min-number"
                        type="number"
                        min="0"
                        max="199"
                        value={minNumber}
                        onChange={(e) => setMinNumber(parseInt(e.target.value) || 0)}
                        className="mt-2 bg-secondary/50 border-white/10"
                      />
                    </div>
                    <div>
                      <Label htmlFor="max-number">Highest Number</Label>
                      <Input
                        id="max-number"
                        type="number"
                        min="0"
                        max="199"
                        value={maxNumber}
                        onChange={(e) => setMaxNumber(parseInt(e.target.value) || 9)}
                        className="mt-2 bg-secondary/50 border-white/10"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This will create {Math.max(0, maxNumber - minNumber + 1)} number slots to upload (from {minNumber} to {maxNumber})
                  </p>
                  <Button
                    onClick={handleCreateSet}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    Create Set
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
  );

  return (
    <MainLayout title="Number Assets" subtitle="Manage your number image sets" headerActions={headerActions}>
      <p className="text-muted-foreground mb-6">
        Create and manage multiple number sets. Each set contains images for numbers 0-9 that can be used in your banners.
      </p>

      {numberSets.length === 0 ? (
        <div className="text-center py-16" data-testid="empty-state">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted/20 mb-4">
            <svg className="w-10 h-10 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">No number sets yet</h3>
          <p className="text-muted-foreground mb-6">Create your first number set to get started</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex gap-4 flex-wrap">
            {numberSets.map((set) => {
              const totalNumbers = (set.max_number || 9) - (set.min_number || 0) + 1;
              const uploadedCount = Object.keys(set.numbers || {}).length;
              return (
                <Button
                  key={set.id}
                  onClick={() => setSelectedSet(set)}
                  variant={selectedSet?.id === set.id ? "default" : "outline"}
                  className={selectedSet?.id === set.id ? "bg-primary text-primary-foreground" : "border-white/10 text-foreground hover:bg-white/5"}
                >
                  {set.name}
                  <span className="ml-2 text-xs opacity-70">({uploadedCount}/{totalNumbers})</span>
                </Button>
              );
            })}
            </div>

            {selectedSet && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">{selectedSet.name}</h2>
                    <p className="text-sm text-muted-foreground">
                      Range: {selectedSet.min_number || 0} - {selectedSet.max_number || 9}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleDeleteSet(selectedSet.id)}
                    variant="destructive"
                    size="sm"
                  >
                    Delete Set
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-4">
                  {Array.from(
                    { length: (selectedSet.max_number || 9) - (selectedSet.min_number || 0) + 1 },
                    (_, i) => (selectedSet.min_number || 0) + i
                  ).map((number) => {
                    const imageData = selectedSet.numbers?.[number.toString()];
                    return (
                      <Card
                        key={number}
                        data-testid={`number-asset-${number}`}
                        className="glass-effect border-white/10 hover:border-primary/30 transition-all"
                      >
                        <CardHeader className="p-3">
                          <CardTitle className="text-center text-lg font-mono text-foreground">{number}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="aspect-square rounded-lg overflow-hidden bg-secondary/20 flex items-center justify-center mb-3 border border-white/10">
                            {imageData ? (
                              <img
                                src={imageData}
                                alt={`Number ${number}`}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <span className="text-4xl text-muted-foreground font-mono">{number}</span>
                            )}
                          </div>
                          <Button
                            onClick={() => handleFileSelect(number)}
                            disabled={uploading}
                            data-testid={`upload-button-${number}`}
                            size="sm"
                            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-xs"
                          >
                            {imageData ? "Replace" : "Upload"}
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0F172A] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete Number Set</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to delete this number set? This action cannot be undone and will permanently remove the set and all uploaded number images.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-secondary hover:bg-secondary/80 text-foreground border-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              Delete Set
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}